import { useCallback, useMemo, useState } from 'react';

import { emptyObject } from 'ui-components/default-props/empty';

import type { CostReviewRateShopperRecommendationMap, CostReviewSelectedRecommendations } from '../../prop-types/cost-review.types';
import type { RateShopperRecommendationSummary } from '../../fulfillment-planner/rate-recommendation/types/rate-recommendation-api-types';

const buildRateShopperRecommendationKey = (fromSellerProviderId: number, fromServiceId: number, toServiceCollectionGuid: string) => {
  return `${fromSellerProviderId}-${fromServiceId}-${toServiceCollectionGuid}`;
};

const buildRateShopperRecommendations = (
  summaryRateShopperRecommendations: RateShopperRecommendationSummary[],
): CostReviewRateShopperRecommendationMap => {
  return summaryRateShopperRecommendations.reduce<CostReviewRateShopperRecommendationMap>((acc, rec) => {
    const { fromSellerProviderId, fromServiceId, toServiceCollectionGuid, toServiceId, totalSavings } = rec;

    return {
      ...acc,
      [fromSellerProviderId]: {
        ...acc[fromSellerProviderId],
        [fromServiceId]: {
          ...acc[fromSellerProviderId]?.[fromServiceId],
          [toServiceCollectionGuid]: {
            key: buildRateShopperRecommendationKey(fromSellerProviderId, fromServiceId, toServiceCollectionGuid),
            fromSellerProviderId: `${fromSellerProviderId}`,
            fromServiceId: `${fromServiceId}`,
            toServiceCollectionGuid,
            toServiceId: `${toServiceId}`,
            totalSavings,
          },
        },
      },
    };
  }, {});
};

export const useCostReviewRateShopperRecommendations = (
  rateShopperRecommendationSummary: RateShopperRecommendationSummary[] | undefined,
) => {
  const [selectedRateShopperRecommendations, setSelectedRateShopperRecommendations] = useState<CostReviewSelectedRecommendations>({});

  const rateShopperRecommendations = useMemo(() => {
    return rateShopperRecommendationSummary ? buildRateShopperRecommendations(rateShopperRecommendationSummary) : emptyObject;
  }, [rateShopperRecommendationSummary]);

  const toggleRateShopperRecommendation = useCallback((recommendationKey: string) => {
    setSelectedRateShopperRecommendations((prev) => {
      return {
        ...prev,
        [recommendationKey]: !prev[recommendationKey],
      };
    });
  }, []);

  const selectAllRateShopperRecommendations = useCallback(() => {
    if (!rateShopperRecommendationSummary) {
      return;
    }

    const allRecommendationsSelected = rateShopperRecommendationSummary.reduce<CostReviewSelectedRecommendations>((acc, recommendation) => {
      const recommendationKey = buildRateShopperRecommendationKey(
        recommendation.fromSellerProviderId,
        recommendation.fromServiceId,
        recommendation.toServiceCollectionGuid,
      );
      return {
        ...acc,
        [recommendationKey]: true,
      };
    }, {});

    setSelectedRateShopperRecommendations(allRecommendationsSelected);
  }, [rateShopperRecommendationSummary]);

  return {
    selectedRateShopperRecommendations,
    rateShopperRecommendations,
    toggleRateShopperRecommendation,
    selectAllRateShopperRecommendations,
  };
};
